import { clock, fromLocalInput, isSameLocalDay, toLocalInput } from "../../../lib/time.ts";
import { dayLabel } from "../../../lib/timeline.ts";
import { Button } from "../../ui/button.tsx";

const FIVE_MINUTES = 5 * 60_000;

/**
 * A time in 5-minute steps, with the phone's own date and time wheel for any
 * other: the wheel's input lies invisible over the time, so a tap on the time
 * opens it (iOS Safari can't open it from code). The bottle sheet and the
 * edit sheet share it.
 */
export function TimeField({
  label,
  value,
  min,
  max,
  now,
  onChange,
}: {
  label: string;
  value: Date;
  min?: Date;
  max: Date;
  now: Date;
  onChange: (d: Date) => void;
}) {
  const earlier = new Date(value.getTime() - FIVE_MINUTES);
  const later = new Date(value.getTime() + FIVE_MINUTES);
  const day = isSameLocalDay(value, now) ? "Today" : dayLabel(value, now);

  return (
    <div className="mt-4">
      <div className="kicker text-center text-[12px]">{label}</div>
      <div className="mt-1.5 flex items-center justify-center gap-[22px]">
        <Button
          variant="stepper"
          className="text-[20px]"
          disabled={min !== undefined && earlier < min}
          onClick={() => onChange(earlier)}
          aria-label={`${label}: 5 minutes earlier`}
        >
          −5
        </Button>
        <div className="relative min-w-[150px] rounded-sm text-center has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent">
          <div
            className="heading text-[40px] leading-none tracking-[-0.03em] tabular-nums"
            aria-hidden="true"
          >
            {clock(value)}
          </div>
          <div className="kicker mt-1 text-[12px]" aria-hidden="true">
            {day}
          </div>
          <input
            type="datetime-local"
            className="absolute inset-0 size-full cursor-pointer text-[16px] opacity-0"
            value={toLocalInput(value)}
            min={min ? toLocalInput(min) : undefined}
            max={toLocalInput(max)}
            aria-label={`${label}, ${day} ${clock(value)}`}
            onChange={(e) => {
              const picked = fromLocalInput(e.target.value);
              if (picked) onChange(picked);
            }}
            onClick={(e) => {
              // A finger's tap opens the wheel itself; a mouse's would only
              // focus the hidden field.
              if (!window.matchMedia("(pointer: fine)").matches) return;
              try {
                e.currentTarget.showPicker();
              } catch {
                // Not allowed here: the field still takes typing.
              }
            }}
          />
        </div>
        <Button
          variant="stepper"
          className="text-[20px]"
          disabled={later > max}
          onClick={() => onChange(later)}
          aria-label={`${label}: 5 minutes later`}
        >
          +5
        </Button>
      </div>
    </div>
  );
}
